import type { CountryCode, PhoneNumber } from 'libphonenumber-js';

import { parsePhoneNumberWithError } from 'libphonenumber-js';

export const SHORT_PHONE_NUMBER_LENGTH = 6;

/**
 * Options for formatting a phone number.
 */
type FormatPhoneNumberOptions = {
  /**
   * The country code to use when parsing the phone number.
   * Defaults to 'US' if not provided.
   * @example 'US', 'CA', 'GB', 'FR'
   */
  countryCode?: CountryCode;
  /**
   * The format style for the returned phone number.
   * - 'national': Format as national number (e.g., "(555) 123-4567")
   * - 'international': Format with country code (e.g., "+1 555 123 4567")
   * - 'auto': Use national format if the number's country matches countryCode, otherwise international
   * @default 'auto'
   */
  format?: 'national' | 'international' | 'auto';
};

/**
 * Result of formatting a phone number.
 */
type FormattedPhoneNumber = {
  /** The formatted phone number string */
  number: string;
  /** The detected country code (e.g., 'US', 'CA'), if successfully parsed */
  country?: CountryCode;
  /** The national number without country code, if successfully parsed */
  nationalNumber?: string;
};

/**
 * Formats a phone number string according to libphonenumber standards.
 *
 * This function parses and formats phone numbers using the libphonenumber-js library,
 * which provides accurate parsing and formatting for international phone numbers.
 *
 * @param number - The phone number string to format (can include spaces, dashes, parentheses, or '+')
 * @param options - Formatting options including country code and format style
 * @returns An object containing the formatted number and metadata (country, nationalNumber)
 *
 * @example
 * // Format a US number with auto formatting
 * formatPhoneNumber('5551234567', { countryCode: 'US' })
 * // Returns: { number: '(555) 123-4567', country: 'US', nationalNumber: '5551234567' }
 *
 * @example
 * // Format with explicit international format
 * formatPhoneNumber('+1 555 123 4567', { format: 'international' })
 * // Returns: { number: '+1 555 123 4567', country: 'US', nationalNumber: '5551234567' }
 *
 * @example
 * // Short numbers (< 6 digits) return normalized without parsing
 * formatPhoneNumber('1234')
 * // Returns: { number: '1234' }
 *
 * @example
 * // Invalid numbers return the original trimmed string
 * formatPhoneNumber('invalid', { countryCode: 'US' })
 * // Returns: { number: 'invalid' }
 */
export const formatPhoneNumber = (
  number: string,
  options?: FormatPhoneNumberOptions,
): FormattedPhoneNumber => {
  const phone = number.trim();
  const normalizedPhone = phone.replaceAll(' ', '').replace('+', '');

  if (normalizedPhone.length <= SHORT_PHONE_NUMBER_LENGTH) {
    return { number: normalizedPhone };
  }

  let parsedPhone: PhoneNumber;

  try {
    parsedPhone = parsePhoneNumberWithError(phone, options?.countryCode ?? 'US');
  } catch {
    return { number: phone };
  }

  const shouldUseNationalFormat = options?.format === 'national'
    || (
      (options?.format === 'auto' || options?.format === undefined)
      && parsedPhone.country === options?.countryCode
    );

  if (shouldUseNationalFormat) {
    return {
      number: parsedPhone.formatNational(),
      country: parsedPhone.country,
      nationalNumber: parsedPhone.nationalNumber,
    };
  }

  return {
    number: parsedPhone.formatInternational(),
    country: parsedPhone.country,
    nationalNumber: parsedPhone.nationalNumber,
  };
};

export const formatNumber = (number: number | string, negative?: boolean) => {
  const value = typeof number === 'number' ? number.toString() : number;

  if (value === '0' && negative) {
    return '-';
  }
  return value.replace(/\B(?=(?:\d{3})+(?!\d))/g, ',');
};

export const formatCurrency = (number: number) => {
  return `$ ${formatNumber(number / 100)}`;
};

type Fn = (...args: any[]) => any;

export function throttle<T extends Fn>(func: T, delay = 500) {
  let throttling = false;

  return (...args: Parameters<T>) => {
    if (!throttling) {
      throttling = true;
      func(...args);
      setTimeout(() => {
        throttling = false;
      }, delay);
    }
  };
}

export const isPhoneNumber = (phoneNumber: string) => /^[\d\s+\-()]+$/.test(phoneNumber);
